import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles'

import Loader from '../../../components/Loader';
import Appointment from '../Appointment';
import style from './AppointmentList.scss';

const localMessages = defineMessages({
  appointmentsTitle: {
    id: 'schedule.appointmentsTitle',
    defaultMessage: 'Scheduled assessments will show up here.',
    description: 'The schedule appointments title',
  },
  appointmentsSHSTitle: {
    id: 'schedule.appointmentsSHSTitle',
    defaultMessage: 'Scheduling Assessments for specialized high schools is your application.',
    description: 'The SHS schedule appointments title',
  },
  appointmentsSHS: {
    id: 'schedule.appointmentsSHS',
    defaultMessage: 'You are currently scheduled to take the following assessments for specialized high schools:',
    description: 'The schedule appointments title description',
  },
  appointmentsGTTitle: {
    id: 'schedule.appointmentsGTTitle',
    defaultMessage: 'Scheduling Assessments for GT is your application.',
    description: 'The GT schedule appointments title',
  },
  appointmentsGT: {
    id: 'schedule.appointmentsGT',
    defaultMessage: 'You are currently scheduled to take the following assessments for GT:',
    description: 'The schedule appointments title description',
  },
});

const AppointmentList = ({
  appointments,
  expressedInterests,
  noMatchingTimeSlots,
  selectedDirectoryProgramId=null,
  showSchoolDetail,
  isSHS,
  isGT,
  isMS,
  programs,
  programClickAppointment=null,
}) => {
  if (appointments.loading || expressedInterests.loading || noMatchingTimeSlots.loading) {
    return <Loader />;
  }

  if (
    appointments.result.length === 0 &&
    expressedInterests.result.length === 0 &&
    noMatchingTimeSlots.result.length === 0
  ) {
    return (
      <div className={style.emptyAppointmentList}>
        <FormattedMessage {...localMessages.appointmentsTitle} />
      </div>
    );
  }

  // Variable to ensure only the ticket label/button is only shown for the first Laguardia appointment
  let isHiddenLaguardiaTicket = false;
  const checkLaguardiaStatus = appointment => {
    // make sure nesting is all available and that the appointment is for Laguardia
    if (appointment.time_slot && appointment.time_slot.school && appointment.time_slot.school.dbn === '03M485') {
      if (!isHiddenLaguardiaTicket) {
        isHiddenLaguardiaTicket = true;
        return false;
      }
      return true;
    }
    return false;
  };

  return (
    <div>
      {appointments.result.map((appointment, idx) => {
        let program;
        const { ticket } = appointment;

        if (isGT && appointment.program && appointment.program.is_shared) {
          program = appointment.program;
        } else if (isMS && appointment.program && appointment.program.is_shared && idx === 0) {
          program = appointment.program;
        } else {
          program = programs.result.find(p => p.id === appointment.program.id);
        }

        const location =
          appointment.time_slot && appointment.time_slot.school
            ? `${appointment.time_slot.school.name}, ${appointment.time_slot.school.full_address}`
            : null;
        const selected =
          program &&
          !program.is_shared &&
          program.directory_program &&
          selectedDirectoryProgramId === program.directory_program.id;
        return program ? (
          <Appointment
            key={`appointment-${appointment.id}`}
            program={program}
            location={location}
            appointmentDate={appointment.time_slot.start_at}
            selected={selected}
            showSchoolDetail={showSchoolDetail}
            isSHS={isSHS}
            isGT={isGT}
            isMS={isMS}
            programClickAppointment={programClickAppointment}
            ticket={ticket}
            hideTicket={checkLaguardiaStatus(appointment)}
          />
        ) : null;
      })}
      {expressedInterests.result.map(expressedInterest => {
        let program;
        if (isMS && expressedInterest.program && expressedInterest.program.is_shared) {
          program = expressedInterest.program;
        } else {
          program = programs.result.find(p => p.id === expressedInterest.program.id);
        }

        let location =
          expressedInterest.time_slot && expressedInterest.time_slot.school
            ? `${expressedInterest.time_slot.school.name}, ${expressedInterest.time_slot.school.full_address}`
            : null;
        if (isMS) {
          location =
            expressedInterest.program && expressedInterest.program.school && expressedInterest.program.school.location;
        }

        const selected =
          program &&
          !program.is_shared &&
          program.directory_program &&
          selectedDirectoryProgramId === program.directory_program.id;

        return program ? (
          <Appointment
            key={`expressedInterest-${expressedInterest.id}`}
            program={program}
            location={location}
            isExpressionOfInterest
            selected={selected}
            showSchoolDetail={showSchoolDetail}
            programClickAppointment={programClickAppointment}
            isSHS={isSHS}
            isGT={isGT}
            isMS={isMS}
          />
        ) : null;
      })}
      {noMatchingTimeSlots.result.map(noMatchingTimeSlot => {
        let program;
        if (isSHS && noMatchingTimeSlot.program && noMatchingTimeSlot.program.is_shared) {
          program = noMatchingTimeSlot.program;
        } else if (isGT && noMatchingTimeSlot.program && noMatchingTimeSlot.program.is_shared) {
          program = noMatchingTimeSlot.program;
        } else {
          program = programs.result.find(p => p.id === noMatchingTimeSlot.program.id);
        }

        const selected =
          program &&
          !program.is_shared &&
          program.directory_program &&
          selectedDirectoryProgramId === program.directory_program.id;

        return program ? (
          <Appointment
            key={`noMatchingTimeSlot-${noMatchingTimeSlot.id}`}
            program={program}
            isNoMatchingTimeSlot
            selected={selected}
            showSchoolDetail={showSchoolDetail}
            programClickAppointment={programClickAppointment}
            isSHS={isSHS}
            isGT={isGT}
            isMS={isMS}
          />
        ) : null;
      })}
    </div>
  );
};

AppointmentList.propTypes = {
  appointments: PropTypes.shape().isRequired,
  expressedInterests: PropTypes.shape().isRequired,
  noMatchingTimeSlots: PropTypes.shape().isRequired,
  selectedDirectoryProgramId: PropTypes.number,
  showSchoolDetail: PropTypes.func.isRequired,
  programs: PropTypes.shape().isRequired,
  isSHS: PropTypes.bool.isRequired,
  isGT: PropTypes.bool.isRequired,
  isMS: PropTypes.bool.isRequired,
  programClickAppointment: PropTypes.func,
};

export default withStyles(style)(injectIntl(AppointmentList));
